import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, MapPin, Building2, Calendar, Home,
  TrendingUp, Wrench, Users, Target, CheckCircle2, DollarSign,
  BarChart3, Lightbulb, ChevronRight, Shield, Award, Package,
  LineChart, Rocket, Clock
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { trackRecord, calendlyLink } from '../../data/mock';
import { CaseStudySchema } from '../../components/StructuredData';

const HollyvaleApartmentsPage = () => {
  // Get Hollyvale property data from trackRecord
  const property = trackRecord.find(p => p.name.includes('Hollyvale')) || trackRecord[1];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Property details
  const propertyDetails = {
    name: 'Hollyvale Apartments',
    location: 'Houston, TX',
    submarket: 'Greater Houston Metropolitan Area',
    county: 'Harris County',
    units: '40 Units + 40 Storage',
    assetClass: 'Multifamily + Storage',
    acquisitionYear: 2013,
    status: 'Realized',
    irr: '28%',
    multiple: '2.4x',
    image: property.image
  };

  // Market opportunity points
  const marketOpportunity = [
    { title: 'Submarket Conditions', description: 'Identified favorable market dynamics in the Houston multifamily sector with strong rental demand and limited new supply in the target submarket' },
    { title: 'Pricing Advantage', description: 'Acquired at significant discount to replacement cost due to operational inefficiencies and deferred maintenance' },
    { title: 'Distressed Situation', description: 'Previous ownership faced management challenges creating opportunity for operational turnaround' },
    { title: 'Value-Add Potential', description: 'Clear path to NOI improvement through renovations, storage monetization, and professional management' }
  ];

  // Value-add business plan
  const valueAddPlan = [
    { title: 'Unit Renovations', description: 'Targeted interior upgrades including flooring, fixtures, appliances, and paint to command market rents' },
    { title: 'Storage Monetization', description: 'Optimized the 40-unit storage component as ancillary income stream with improved marketing and pricing' },
    { title: 'Operational Restructuring', description: 'Implemented institutional-grade property management practices and expense controls' },
    { title: 'Exterior Improvements', description: 'Enhanced curb appeal through landscaping, facade improvements, and common area upgrades' },
    { title: 'Security Upgrades', description: 'Installed improved lighting, access control, and surveillance systems' },
    { title: 'Tenant Repositioning', description: 'Improved tenant screening and retention programs to reduce turnover costs' }
  ];

  // Execution timeline
  const executionTimeline = [
    { phase: 'Year 1', title: 'Stabilization', description: 'Property assessment, management transition, initial capital improvements' },
    { phase: 'Year 2-3', title: 'Value Creation', description: 'Unit renovations, storage optimization, occupancy improvements' },
    { phase: 'Year 4-5', title: 'Optimization', description: 'Continued operational refinements, rent growth, expense management' },
    { phase: 'Exit', title: 'Disposition', description: 'Strategic sale to maximize investor returns at favorable market conditions' }
  ];

  // Operational highlights
  const operationalHighlights = [
    { metric: 'Occupancy Improvement', description: 'Achieved and maintained stabilized occupancy through improved marketing and tenant retention' },
    { metric: 'Revenue Growth', description: 'Increased rental income through unit renovations and storage monetization' },
    { metric: 'Expense Efficiency', description: 'Reduced operating costs through vendor renegotiations and operational improvements' },
    { metric: 'NOI Enhancement', description: 'Significant improvement in net operating income supporting valuation growth' }
  ];

  // Exit results
  const exitResults = {
    irr: '28%',
    multiple: '2.4x',
    holdPeriod: 'Multi-year hold',
    exitStrategy: 'Strategic disposition at favorable market conditions'
  };

  // Key lessons
  const keyLessons = [
    'Workforce housing in Greater Houston demonstrates resilient demand across economic cycles',
    'Ancillary income streams like storage can meaningfully enhance property-level returns',
    'In-house property management provides critical control for value-add execution',
    'Patient capital and disciplined operations create sustainable value over market timing'
  ];

  // Other case studies for navigation
  const otherCaseStudies = trackRecord.filter(p => !p.name.includes('Hollyvale')).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Hollyvale Apartments Case Study | Houston Multifamily Investment | ASAP Capital</title>
        <meta 
          name="description" 
          content="Realized multifamily investment case study of Hollyvale Apartments in Houston, highlighting value-add strategy, renovations, and exit performance." 
        />
        <meta name="keywords" content="Hollyvale Apartments, Houston multifamily investment, realized investment case study, value add apartment investment, multifamily repositioning, workforce housing Houston, real estate private equity case study" />
        <link rel="canonical" href="/case-studies/hollyvale-apartments" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-0 lg:pt-40 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              {/* Breadcrumb */}
              <motion.div 
                className="flex items-center gap-2 text-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Link to="/case-studies" className="text-white/50 hover:text-[#D4AF37] transition-colors">
                  Case Studies
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
                <span className="text-white/70">Hollyvale Apartments</span>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-10 items-end"
              >
                {/* Left - Content */}
                <div className="pb-10 lg:pb-16">
                  <motion.div 
                    className="flex items-center gap-3 mb-4"
                    variants={itemVariants}
                  >
                    <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      {propertyDetails.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs border border-white/10">
                      {propertyDetails.assetClass}
                    </span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-2"
                    variants={itemVariants}
                  >
                    Hollyvale Apartments Multifamily Investment Case Study
                  </motion.h1>

                  <motion.p 
                    className="text-lg text-[#D4AF37] mb-6"
                    variants={itemVariants}
                  >
                    Realized Houston Multifamily Investment
                  </motion.p>

                  <motion.div 
                    className="flex flex-wrap gap-6 text-white/60 text-sm mb-6"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      <span>Houston, Texas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-[#D4AF37]" />
                      <span>{propertyDetails.units}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#D4AF37]" />
                      <span>Acquired {propertyDetails.acquisitionYear}</span>
                    </div>
                  </motion.div>

                  {/* Performance Highlights */}
                  <motion.div 
                    className="flex gap-6 mb-6"
                    variants={itemVariants}
                  >
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-5 py-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{propertyDetails.irr}</p>
                      <p className="text-xs text-white/50">Realized IRR</p>
                    </div>
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl px-5 py-3 text-center">
                      <p className="text-2xl font-bold text-[#D4AF37]">{propertyDetails.multiple}</p>
                      <p className="text-xs text-white/50">Equity Multiple</p>
                    </div>
                  </motion.div>

                  <motion.p 
                    className="text-white/50 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Past performance is not indicative of future results. Investment opportunities are offered only through a Private Placement Memorandum to investors.
                  </motion.p>
                </div>

                {/* Right - Image */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="relative rounded-t-2xl lg:rounded-2xl overflow-hidden">
                    <img 
                      src={propertyDetails.image}
                      alt={propertyDetails.name}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
                    
                    {/* Realized badge overlay */}
                    <div className="absolute top-4 right-4">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30">
                        <Award className="h-4 w-4 text-emerald-400" />
                        <span className="text-emerald-400 text-sm font-semibold">Successfully Realized</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROPERTY OVERVIEW ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Building2 className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Property Overview
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {[
                    { label: 'Location', value: 'Houston, TX', subValue: propertyDetails.submarket, icon: MapPin },
                    { label: 'Asset Type', value: 'Multifamily + Storage', subValue: '40 Units + 40 Storage', icon: Building2 },
                    { label: 'Acquisition', value: '2013', subValue: 'Year Acquired', icon: Calendar },
                    { label: 'Status', value: 'Realized', subValue: 'Successfully Exited', icon: Award, highlight: true },
                    { label: 'Performance', value: '28% IRR', subValue: '2.4x Multiple', icon: TrendingUp, highlight: true }
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      className={`bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-5 border ${item.highlight ? 'border-emerald-500/30' : 'border-white/10'}`}
                      variants={itemVariants}
                    >
                      <item.icon className={`h-5 w-5 mb-3 ${item.highlight ? 'text-emerald-400' : 'text-[#D4AF37]'}`} />
                      <p className="text-white/40 text-xs mb-1">{item.label}</p>
                      <p className={`font-bold text-lg ${item.highlight ? 'text-emerald-400' : 'text-white'}`}>{item.value}</p>
                      {item.subValue && <p className="text-white/50 text-sm">{item.subValue}</p>}
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 p-5 bg-white/5 rounded-xl border border-white/5"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/70 font-medium mb-1">Location Details</p>
                      <p className="text-white/50 text-sm">{propertyDetails.location}</p>
                      <p className="text-white/40 text-sm mt-1">{propertyDetails.county}, {propertyDetails.submarket}</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET OPPORTUNITY AND ACQUISITION STRATEGY ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Target className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Market Opportunity and Acquisition Strategy
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Hollyvale Apartments presented a compelling value-add opportunity in Houston's 
                  workforce housing segment. The property was acquired at an attractive basis with 
                  clear operational upside through renovations, storage monetization, and 
                  professional management implementation.
                </motion.p>

                <div className="grid md:grid-cols-2 gap-6">
                  {marketOpportunity.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5 hover:border-blue-500/30 transition-all"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                        <CheckCircle2 className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                        <p className="text-white/50 text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== VALUE-ADD BUSINESS PLAN ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                    <Wrench className="h-5 w-5 text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Value-Add Business Plan
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  The business plan focused on comprehensive improvements designed to enhance 
                  property performance, including unit renovations, storage optimization, and 
                  operational restructuring to maximize NOI and investor returns.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {valueAddPlan.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 hover:border-amber-500/30 transition-all"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                        <span className="text-amber-400 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXECUTION AND PROPERTY IMPROVEMENTS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <Clock className="h-5 w-5 text-purple-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Execution and Property Improvements
                  </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-10">
                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Investment Timeline</h4>
                    <div className="space-y-4">
                      {executionTimeline.map((item, index) => (
                        <div key={item.phase} className="flex items-start gap-4">
                          <span className="text-purple-400 font-bold text-sm mt-1 w-16">{item.phase}</span>
                          <div className="flex-1 p-4 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5">
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-white/50 text-sm">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Key Improvement Initiatives</h4>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10">
                      <ul className="space-y-3">
                        {[
                          'Comprehensive unit interior renovations across the property',
                          'Storage facility optimization with improved marketing and pricing',
                          'Exterior facade improvements and landscaping upgrades',
                          'Security system enhancements including lighting and access control',
                          'Professional property management implementation',
                          'Vendor contract renegotiations for cost efficiency',
                          'Enhanced tenant screening and retention programs'
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <span className="text-white/60 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== OPERATIONAL PERFORMANCE AND STABILIZATION ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                    <BarChart3 className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Operational Performance and Stabilization
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Through disciplined execution of the business plan, the property achieved 
                  meaningful improvements in operational metrics throughout the hold period, 
                  positioning the asset for a successful exit.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {operationalHighlights.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5, borderColor: 'rgba(6, 182, 212, 0.3)' }}
                    >
                      <h4 className="text-white font-semibold mb-2">{item.metric}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXIT STRATEGY AND REALIZED RESULTS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Rocket className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Exit Strategy and Realized Results
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  The property was successfully exited through a strategic disposition at favorable 
                  market conditions, delivering strong risk-adjusted returns to investors. The exit 
                  timing aligned with our value creation thesis and market cycle positioning.
                </motion.p>

                <div className="grid lg:grid-cols-2 gap-10">
                  {/* Results Cards */}
                  <motion.div 
                    className="bg-gradient-to-br from-emerald-500/10 to-[#0A1628] rounded-2xl p-8 border border-emerald-500/30"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <Award className="h-6 w-6 text-emerald-400" />
                      <h4 className="text-white font-semibold text-lg">Realized Performance</h4>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div className="text-center p-4 bg-white/5 rounded-xl">
                        <p className="text-4xl font-bold text-emerald-400 mb-1">{exitResults.irr}</p>
                        <p className="text-white/50 text-sm">Internal Rate of Return</p>
                      </div>
                      <div className="text-center p-4 bg-white/5 rounded-xl">
                        <p className="text-4xl font-bold text-[#D4AF37] mb-1">{exitResults.multiple}</p>
                        <p className="text-white/50 text-sm">Equity Multiple</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-emerald-400" />
                        <span className="text-white/60 text-sm">{exitResults.holdPeriod}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-4 w-4 text-emerald-400" />
                        <span className="text-white/60 text-sm">{exitResults.exitStrategy}</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Exit Details */}
                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Exit Execution</h4>
                    <div className="space-y-4">
                      {[
                        { title: 'Market Timing', description: 'Exit aligned with favorable market conditions and strong buyer demand for stabilized multifamily assets' },
                        { title: 'Value Realization', description: 'Improvements and operational enhancements fully reflected in exit valuation' },
                        { title: 'Capital Return', description: 'Full return of invested capital plus profit distributions to investors' },
                        { title: 'Investment Thesis Validation', description: 'Results demonstrated successful execution of value-add strategy' }
                      ].map((item, index) => (
                        <div key={item.title} className="flex items-start gap-3 p-4 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5">
                          <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-white/50 text-sm">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Disclaimer */}
                <motion.div 
                  className="mt-8 bg-white/[0.02] border border-white/5 rounded-xl p-4"
                  variants={itemVariants}
                >
                  <p className="text-white/40 text-xs text-center">
                    <Shield className="h-4 w-4 inline-block mr-2 text-white/30" />
                    Past performance is not indicative of future results. The returns shown are historical and 
                    were achieved under specific market conditions. All investments involve substantial risk 
                    including potential loss of principal. Please refer to offering documents for complete details.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== KEY LESSONS AND INVESTMENT INSIGHTS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Lightbulb className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Key Lessons and Investment Insights
                  </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {keyLessons.map((lesson, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5"
                      variants={itemVariants}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/20">
                        <span className="text-[#D4AF37] font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-white/60">{lesson}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY FIT ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                      <LineChart className="h-5 w-5 text-blue-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      How This Deal Reflects Our Investment Strategy
                    </h2>
                  </div>

                  <p className="text-white/60 mb-6">
                    Hollyvale Apartments exemplifies our value-add multifamily investment approach. 
                    By acquiring an underperforming asset, implementing targeted improvements, and 
                    executing a disciplined business plan, we demonstrated our ability to generate 
                    attractive risk-adjusted returns for investors.
                  </p>

                  <p className="text-white/60 mb-6">
                    This realized investment validates our investment thesis and operational 
                    capabilities, providing tangible proof of our ability to execute full 
                    lifecycle value creation in the Houston market.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/strategy">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full">
                        View Investment Strategy
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/track-record">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full">
                        View Track Record
                      </Button>
                    </Link>
                  </div>
                </motion.div>

                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6">Our Value-Add Process</h4>
                  <div className="space-y-4">
                    {[
                      { title: 'Identify Opportunity', description: 'Source undervalued assets with clear value creation potential' },
                      { title: 'Execute Business Plan', description: 'Implement renovations, operational improvements, and repositioning' },
                      { title: 'Optimize Operations', description: 'Apply institutional management to maximize NOI' },
                      { title: 'Realize Value', description: 'Exit at optimal timing to maximize investor returns' }
                    ].map((item, index) => (
                      <div key={item.title} className="flex items-start gap-4">
                        <span className="text-[#D4AF37] font-bold">{String(index + 1).padStart(2, '0')}</span>
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-white/50 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXPLORE MORE CTA ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Explore More Investment Opportunities
                </h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                  Review additional examples of our value-add investment approach across Houston's 
                  multifamily and commercial real estate markets.
                </p>
              </motion.div>

              {/* Other Case Studies */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {otherCaseStudies.map((study) => (
                  <motion.div
                    key={study.id}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[#D4AF37] text-sm mb-1">{study.type}</p>
                      <h4 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {study.name}
                      </h4>
                      <p className="text-white/50 text-sm">{study.location}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <Link to="/case-studies">
                  <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20">
                    View All Case Studies
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full">
                    View Portfolio
                  </Button>
                </Link>
                <motion.a 
                  href={calendlyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-3 h-12 rounded-full">
                    Schedule Consultation
                    <Calendar className="ml-2 h-4 w-4" />
                  </Button>
                </motion.a>
              </motion.div>
            </div>
          </section>

          {/* ==================== BACK LINK ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <Link 
                to="/case-studies"
                className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Case Studies
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HollyvaleApartmentsPage;
